import type { StudentQuestionAnswer, StudentTestExercise } from "p6m-p6u";
import type { CSSProperties } from "react";
import { useState } from "react";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import type { ConnectWordsColor } from "../../../constants/ConnectColors";
import { ConnectWordsColors } from "../../../constants/ConnectColors";
import { getExerciseProgress } from "../../../helpers/OnlineTestHelper";
import ExerciseWrapper from "./ExerciseWrapper";

interface ConnectWordsExerciseProps {
    exercise: StudentTestExercise;
    exerciseIndex: number;
    studentAnswer: StudentQuestionAnswer;
    onAnswerUpdate: (updatedAnswer: StudentQuestionAnswer, exerciseId: string) => void;
}

const GREY_LIGHT = "var(--color-p6-grey-light)";
const CARD_BASE_CLASSES = "min-w-0 rounded-medium font-semibold text-m text-center cursor-pointer break-words";

const ConnectWordsExercise = (props: ConnectWordsExerciseProps) => {
    const [selectedLeftIndex, setSelectedLeftIndex] = useState<number | null>(null);
    const [selectedRightIndex, setSelectedRightIndex] = useState<number | null>(null);
    const [selectedRightColor, setSelectedRightColor] = useState<ConnectWordsColor | null>(null);

    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const exerciseId = props.exercise.id;
    const answers = props.studentAnswer.studentAnswers ?? [];
    const connectWordsAnswers = props.studentAnswer.connectWordsAnswers ?? [];

    const isAnswerConnected = (item: { studentAnswer?: string }) => !!item.studentAnswer?.trim();
    const progress = getExerciseProgress(props.studentAnswer, props.exercise.questionMode);

    const clearSelection = () => {
        setSelectedLeftIndex(null);
        setSelectedRightIndex(null);
        setSelectedRightColor(null);
    };

    // Colors are assigned at initialization and persisted through break/reconnect — only swapped, never cleared
    const getLeftCardColor = (leftIndex: number): ConnectWordsColor => {
        return (
            ConnectWordsColors.find((c) => c.name === answers[leftIndex]?.color) ??
            ConnectWordsColors[leftIndex % ConnectWordsColors.length]
        );
    };

    const getFirstAvailableColor = (): ConnectWordsColor => {
        const usedColorNames = new Set(answers.filter((a) => isAnswerConnected(a) && a.color).map((a) => a.color));
        return ConnectWordsColors.find((color) => !usedColorNames.has(color.name)) ?? ConnectWordsColors[0];
    };

    // Matches by right-card word text stored in studentAnswer
    const getColorForRightCard = (rightIndex: number): ConnectWordsColor | null => {
        const connectedLeftIndex = answers.findIndex((a) => a.studentAnswer === connectWordsAnswers[rightIndex]);
        return connectedLeftIndex !== -1 ? getLeftCardColor(connectedLeftIndex) : null;
    };

    const makeConnection = (leftIndex: number, rightIndex: number, color?: ConnectWordsColor) => {
        const newAnswers = [...answers];

        const previousLeftForThisRight = newAnswers.findIndex(
            (a) => a.studentAnswer === connectWordsAnswers[rightIndex]
        );
        if (previousLeftForThisRight !== -1 && previousLeftForThisRight !== leftIndex) {
            newAnswers[previousLeftForThisRight] = { ...newAnswers[previousLeftForThisRight], studentAnswer: "" };
        }

        const incomingColor = color ?? getLeftCardColor(leftIndex);
        const existingColor = getLeftCardColor(leftIndex);

        // If another left card already uses the incoming color, swap it to avoid duplicates
        if (incomingColor.name !== existingColor.name) {
            const conflictIndex = newAnswers.findIndex(
                (_, i) => i !== leftIndex && getLeftCardColor(i).name === incomingColor.name
            );
            if (conflictIndex !== -1) {
                newAnswers[conflictIndex] = { ...newAnswers[conflictIndex], color: existingColor.name };
            }
        }

        newAnswers[leftIndex] = {
            ...newAnswers[leftIndex],
            studentAnswer: connectWordsAnswers[rightIndex],
            color: incomingColor.name,
        };

        props.onAnswerUpdate({ ...props.studentAnswer, studentAnswers: newAnswers }, exerciseId!);
    };

    const breakConnection = (leftIndex: number) => {
        const newAnswers = [...answers];
        newAnswers[leftIndex] = { ...newAnswers[leftIndex], studentAnswer: "" };
        props.onAnswerUpdate({ ...props.studentAnswer, studentAnswers: newAnswers }, exerciseId!);
    };

    const handleLeftCardClick = (leftIndex: number) => {
        if (selectedLeftIndex === leftIndex) {
            setSelectedLeftIndex(null);
            return;
        }

        if (selectedRightIndex !== null) {
            makeConnection(leftIndex, selectedRightIndex, selectedRightColor ?? undefined);
            clearSelection();
            return;
        }

        const isConnected = isAnswerConnected(answers[leftIndex]);
        if (isConnected) {
            breakConnection(leftIndex);
        }

        setSelectedLeftIndex(leftIndex);
        setSelectedRightIndex(null);
        setSelectedRightColor(null);
    };

    const handleRightCardClick = (rightIndex: number) => {
        if (selectedRightIndex === rightIndex) {
            setSelectedRightIndex(null);
            setSelectedRightColor(null);
            return;
        }

        if (selectedLeftIndex !== null) {
            makeConnection(selectedLeftIndex, rightIndex);
            clearSelection();
            return;
        }

        const connectedLeftIndex = answers.findIndex((a) => a.studentAnswer === connectWordsAnswers[rightIndex]);

        if (connectedLeftIndex !== -1) {
            const connectionColor = getLeftCardColor(connectedLeftIndex);
            breakConnection(connectedLeftIndex);
            setSelectedRightIndex(rightIndex);
            setSelectedRightColor(connectionColor);
            setSelectedLeftIndex(null);
            return;
        }

        setSelectedRightIndex(rightIndex);
        setSelectedRightColor(getFirstAvailableColor());
        setSelectedLeftIndex(null);
    };

    const getCardDynamicStyles = (color: ConnectWordsColor | null): CSSProperties => ({
        borderColor: color ? color.borderColor : GREY_LIGHT,
        backgroundColor: color ? color.bgColor : "var(--color-white)",
    });

    const renderLeftCard = (index: number) => {
        const item = answers[index];
        const color = getLeftCardColor(index);
        const isSelected = selectedLeftIndex === index;
        const isConnected = isAnswerConnected(item);
        const hasColor = isSelected || isConnected;

        const textColor = hasColor ? "text-p6-grey-darkest" : "text-p6-grey-dark";
        const borderClass = hasColor ? "border-y-2 border-l-2 border-r-[8px]" : "border-2";
        const paddingClass = hasColor
            ? getStyleBasedOnBreakpoint(Breakpoint.MD, "py-m pl-s pr-s", "py-s pl-xs pr-xs")
            : getStyleBasedOnBreakpoint(Breakpoint.MD, "py-m pl-s pr-[22px]", "py-s pl-xs pr-[14px]");

        return (
            <button
                key={item.id ?? `left-${index}`}
                data-testid={`online-test-connect-left-${index}`}
                type="button"
                aria-pressed={isSelected || isConnected}
                className={`${CARD_BASE_CLASSES} ${borderClass} ${textColor} ${paddingClass}`}
                style={getCardDynamicStyles(hasColor ? color : null)}
                onClick={() => handleLeftCardClick(index)}
            >
                {item.question || ""}
            </button>
        );
    };

    const renderRightCard = (index: number) => {
        const word = connectWordsAnswers[index];
        const connectedColor = getColorForRightCard(index);
        const isSelected = selectedRightIndex === index;

        const selectedColor = isSelected ? selectedRightColor : null;
        const color = connectedColor ?? selectedColor;
        const hasColor = color !== null;

        const textColor = hasColor ? "text-p6-grey-darkest" : "text-p6-grey-dark";
        const borderClass = hasColor ? "border-y-2 border-r-2 border-l-[8px]" : "border-2";
        const paddingClass = hasColor
            ? getStyleBasedOnBreakpoint(Breakpoint.MD, "py-m pr-s pl-s", "py-s pr-xs pl-xs")
            : getStyleBasedOnBreakpoint(Breakpoint.MD, "py-m pr-s pl-[22px]", "py-s pr-xs pl-[14px]");

        return (
            <button
                key={`right-${index}`}
                data-testid={`online-test-connect-right-${index}`}
                type="button"
                aria-pressed={isSelected || connectedColor !== null}
                className={`${CARD_BASE_CLASSES} ${borderClass} ${textColor} ${paddingClass}`}
                style={getCardDynamicStyles(color)}
                onClick={() => handleRightCardClick(index)}
            >
                {word}
            </button>
        );
    };

    const renderContent = () => {
        if (!answers.length || !connectWordsAnswers.length) return null;

        const rowCount = Math.max(answers.length, connectWordsAnswers.length);

        return (
            <div
                className={`grid grid-cols-2 overflow-hidden ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "gap-x-l gap-y-s",
                    "gap-x-s gap-y-xs"
                )}`}
            >
                {Array.from({ length: rowCount }, (_, index) => (
                    <div
                        key={index}
                        className="contents"
                    >
                        {index < answers.length ? renderLeftCard(index) : <div />}
                        {index < connectWordsAnswers.length ? renderRightCard(index) : <div />}
                    </div>
                ))}
            </div>
        );
    };

    if (!exerciseId) return null;

    return (
        <ExerciseWrapper
            data-testid="online-test-exercise-connect-words"
            number={props.exerciseIndex + 1}
            title={props.exercise.extraComments ?? ""}
            progress={progress}
        >
            {renderContent()}
        </ExerciseWrapper>
    );
};

export default ConnectWordsExercise;
